import type { MessageDescriptor } from "@lingui/core";

import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { observable } from "mobx";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";

import { MeasurementRequiredData_Output, StrictPPGConditions } from "../../api";
import { ApiConnector } from "../../api/api-connector";
import { useSupportedMedicalData } from "../../catalogs/supported-medical-data";
import { useSupportedSymptoms } from "../../catalogs/supported-symptoms";
import { Button } from "../../components/ui-kit/button";
import { Dialog } from "../../components/ui-kit/dialog";
import {
  Combobox,
  ComboboxOption,
} from "../../components/ui-kit/fields/combobox";
import { NumberInput } from "../../components/ui-kit/fields/number-input";
import { Select } from "../../components/ui-kit/fields/select";
import { TextInput } from "../../components/ui-kit/fields/text-input";
import { resolveEnumLabel } from "../../components/ui-kit/fields/utils";
import { Form } from "../../components/ui-kit/form/form";
import { FormRow } from "../../components/ui-kit/form/form-row";
import { FormTable } from "../../components/ui-kit/form/form-table";
import { SimpleTable } from "../../components/ui-kit/tables/simple-table";
import { ppgConditionLabels } from "../../constants/enum-to-text";
import { assert } from "../../utils/utils";
import { Patient } from "./patient";

export const SelfMeasurementConfig = observer(
  function SelfMeasurementConfig(props: {
    config: MeasurementRequiredData_Output[];
    api: ApiConnector;
    patient: Patient;
    editable?: boolean;
  }) {
    const [configModel] = useState(
      (): {
        data: ConfigItem[];
      } =>
        observable({
          data: [],
        }),
    );
    const [editing, setEditing] = useState(false);
    const [adding, setAdding] = useState(false);

    const startEdit = () => {
      configModel.data = props.config.slice().sort((a, b) => a.order - b.order);
      setEditing(true);
    };

    const exitEdit = () => {
      setEditing(false);
    };

    return (
      <div>
        <>
          <SimpleTable<ConfigItem>
            rows={editing ? configModel.data : props.config}
            columns={[
              {
                header1: <Trans>Order</Trans>,
                cell: (row, index) =>
                  editing ? (
                    <Select
                      value={index + 1}
                      values={Array.from(
                        { length: configModel.data.length },
                        (_, i) => i + 1,
                      )}
                      onChange={(newOrder) => {
                        const item = configModel.data.splice(index, 1)[0];
                        configModel.data.splice(newOrder - 1, 0, item);
                      }}
                    />
                  ) : (
                    index + 1
                  ),
              },
              {
                header1: <Trans>Type</Trans>,
                cell: (row) => resolveEnumLabel(dataTypes[row.data_type]),
              },
              {
                header1: <Trans>Data name</Trans>,
                cell: (row) =>
                  row.data_name ?? resolveEnumLabel(dataTypes[row.data_type]),
              },
              editing && {
                header1: "",
                cell: (row, index) => (
                  <Button
                    variant="subtle"
                    onClick={() => {
                      configModel.data.splice(index, 1);
                    }}
                  >
                    -
                  </Button>
                ),
              },
            ]}
            foot={
              editing ? (
                <tr>
                  <td colSpan={100}>
                    <Button
                      variant="subtle"
                      onClick={() => {
                        setAdding(true);
                      }}
                    >
                      <Trans>Add</Trans>
                    </Button>
                  </td>
                </tr>
              ) : undefined
            }
          />

          {!editing && props.editable !== false && (
            <Button
              onClick={() => {
                startEdit();
              }}
            >
              <Trans>Edit</Trans>
            </Button>
          )}

          {editing && (
            <div className="flex gap-2">
              <Button
                onClick={async () => {
                  await props.patient.updatePatientField({
                    self_measurement_config: configModel.data.map(
                      (i, index) => ({
                        data_type: i.data_type,
                        order: index + 1,
                        data_name: i.data_name,
                        measurement_condition: i.measurement_condition,
                        minimum_length: i.minimum_length,
                        ppg_device_type: i.ppg_device_type,
                        ecg_device_type: i.ecg_device_type,
                        required_device: i.required_device,
                      }),
                    ),
                  });

                  exitEdit();
                }}
              >
                <Trans>Confirm</Trans>
              </Button>

              <Button
                onClick={() => {
                  exitEdit();
                }}
              >
                <Trans>Cancel</Trans>
              </Button>
            </div>
          )}
        </>

        {adding && (
          <AddingDialog
            onConfirm={(data) => {
              setAdding(false);
              configModel.data.push({
                ...data,
                order: configModel.data.length + 1,
              });
            }}
            onClose={() => setAdding(false)}
          />
        )}
      </div>
    );
  },
);

interface ConfigItem {
  data_type: keyof typeof dataTypes;
  data_name?: string;
  measurement_condition?: StrictPPGConditions;
  minimum_length?: number;
  ppg_device_type?: keyof typeof ppgDeviceTypes;
  ecg_device_type?: keyof typeof ecgDeviceTypes;
  required_device?: string | null;
  order: number;
}

type NewConfigItemDraft = Partial<Omit<ConfigItem, "order">>;

type NewConfigItemBody = Omit<ConfigItem, "order">;

export const AddingDialog = observer(function AddingDialog(props: {
  onConfirm: (data: NewConfigItemBody) => void;
  onClose: () => void;
}) {
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) {
          props.onClose();
        }
      }}
    >
      <AddingDialogBody onConfirm={props.onConfirm} />
    </Dialog>
  );
});

const AddingDialogBody = observer(function AddingDialogBody(props: {
  onConfirm: (data: NewConfigItemBody) => void;
}) {
  const { t } = useLingui();
  const supportedMedData = useSupportedMedicalData();
  const supportedSymptoms = useSupportedSymptoms();
  const [data] = useState((): NewConfigItemDraft => observable({}));

  useEffect(() => {
    // Reset data when dialog opens (component mounts)
    // Clear all properties from the observable object
    if (data.data_type) delete data.data_type;
    if (data.data_name) delete data.data_name;
    if (data.measurement_condition) delete data.measurement_condition;
    if (data.minimum_length) delete data.minimum_length;
    if (data.ppg_device_type) delete data.ppg_device_type;
    if (data.ecg_device_type) delete data.ecg_device_type;
    if (data.required_device) delete data.required_device;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only reset
  }, []);

  return (
    <Form
      onSubmit={() => {
        assert(data.data_type, "Data type is required");
        props.onConfirm({
          ...data,
          data_type: data.data_type,
        });
      }}
      className="flex flex-col"
    >
      <FormTable>
        <FormRow
          label={t`Type`}
          data={
            <Select
              values={dataTypes}
              model={data}
              attr="data_type"
              listTitle={t`Select type`}
              onChange={(newValue) => {
                delete data.data_name;
                delete data.measurement_condition;
                delete data.minimum_length;
                delete data.ppg_device_type;
                delete data.ecg_device_type;
                delete data.required_device;

                if (newValue === "ppg") {
                  data.minimum_length = 120;
                  data.ppg_device_type = "berry";
                }

                if (newValue === "ecg") {
                  data.minimum_length = 30;
                  data.ecg_device_type = "alivecor";
                }
              }}
              required
            />
          }
        />

        {data.data_type === "medical-data" && (
          <FormRow
            label={t`Name`}
            data={
              <Combobox
                model={data}
                attr="data_name"
                options={supportedMedData
                  .filter((data) => data.can_self_report)
                  .map(
                    (d): ComboboxOption<string> => ({
                      value: d.data_name,
                      label: d.data_name,
                    }),
                  )}
              />
            }
          />
        )}

        {data.data_type === "symptom" && (
          <FormRow
            label={t`Name`}
            data={
              <Combobox
                model={data}
                attr="data_name"
                options={supportedSymptoms
                  .filter((data) => data.can_self_report)
                  .map(
                    (d): ComboboxOption<string> => ({
                      value: d.data_name,
                      label: d.data_name,
                    }),
                  )}
              />
            }
          />
        )}

        {data.data_type === "ppg" && (
          <FormRow
            label={t`Condition`}
            data={
              <Select
                values={ppgConditionLabels}
                model={data}
                attr="measurement_condition"
              />
            }
          />
        )}

        {(data.data_type === "ppg" || data.data_type === "ecg") && (
          <FormRow
            label={t`Minimum length (s)`}
            data={<NumberInput model={data} attr="minimum_length" />}
          />
        )}

        {data.data_type === "ppg" && (
          <FormRow
            label={t`PPG device type`}
            data={
              <Select
                values={ppgDeviceTypes}
                model={data}
                attr="ppg_device_type"
              />
            }
          />
        )}

        {data.data_type === "ecg" && (
          <FormRow
            label={t`ECG device type`}
            data={
              <Select
                values={ecgDeviceTypes}
                model={data}
                attr="ecg_device_type"
              />
            }
          />
        )}

        {(data.data_type === "ppg" || data.data_type === "ecg") && (
          <FormRow
            label={t`Required device`}
            data={<TextInput model={data} attr="required_device" />}
          />
        )}
      </FormTable>

      <div className="flex justify-center mt-4">
        <Button type="submit">
          <Trans>Add</Trans>
        </Button>
      </div>
    </Form>
  );
});

export const dataTypes: Record<string, MessageDescriptor> = {
  "medical-data": msg`medical data`,
  symptom: msg`symptom`,
  ppg: msg`PPG`,
  ecg: msg`ECG`,
};

const ppgDeviceTypes = {
  berry: "Berry",
  samsung: "Samsung",
  smartcare: "Smartcare",
} as const;

const ecgDeviceTypes = {
  alivecor: "AliveCor",
} as const;
